import React from 'react';
import {get} from 'lodash';
import className from 'classnames';
import axios from 'axios';

import {
  CSRF_TOKEN,
  requestCallback,
} from '../lib/helpers';

import {
  canEdit,
} from '../lib/policies';

import {useCheckIds} from '../hooks';

import ImageLoader from './ImageLoader';
import OverflowH from './OverflowH';
import DotTooltip from './DotTooltip';
import ModalDelete from './ModalDelete';
import ModalDeleteImage from './ModalDeleteImage';
import TableHeader from './TableHeader';

const GroupsList = ({
  groups = [],
  total = 0,
  enableOpacity = true,
}) => {
  const {
    checkIds,
    renderCheckBox, renderCheckBoxForCheckAll,
    renderOpenAllButton,
  } = useCheckIds({
    data: groups,
    openUrl: 'groups',
  })

  const bulkDelete = () => {
    requestCallback(axios.delete(`/${gon.currentProvider}/api/bulk/groups`, {
      data: {
        ids: checkIds,
        authenticity_token: CSRF_TOKEN
      }
    }))
  }

  const bulkRemoveCover = () => {
    requestCallback(axios.post(`/${gon.currentProvider}/api/bulk/groups/remove_cover`, {
      ids: checkIds,
      authenticity_token: CSRF_TOKEN,
    }))
  }

  const bulkHide = () => {
    requestCallback(axios.post(`/${gon.currentProvider}/api/bulk/groups/hide`, {
      ids: checkIds,
      authenticity_token: CSRF_TOKEN,
    }))
  }

  const bulkUnHide = () => {
    requestCallback(axios.post(`/${gon.currentProvider}/api/bulk/groups/unhide`, {
      ids: checkIds,
      authenticity_token: CSRF_TOKEN,
    }))
  }

  const bulkSafe = () => {
    requestCallback(axios.post(`/${gon.currentProvider}/api/bulk/groups/safe`, {
      ids: checkIds,
      authenticity_token: CSRF_TOKEN,
    }))
  }

  const bulkUnSafe = () => {
    requestCallback(axios.post(`/${gon.currentProvider}/api/bulk/groups/unsafe`, {
      ids: checkIds,
      authenticity_token: CSRF_TOKEN,
    }))
  }

  const hide = (id) => {
    requestCallback(axios.put(`/${gon.currentProvider}/api/groups/${id}/hide`, {
      authenticity_token: CSRF_TOKEN,
    }))
  }

  const unhide = (id) => {
    requestCallback(axios.put(`/${gon.currentProvider}/api/groups/${id}/unhide`, {
      authenticity_token: CSRF_TOKEN,
    }))
  }

  const safe = (id) => {
    requestCallback(axios.put(`/${gon.currentProvider}/api/groups/${id}/safe`, {
      authenticity_token: CSRF_TOKEN,
    }))
  }

  const unsafe = (id) => {
    requestCallback(axios.put(`/${gon.currentProvider}/api/groups/${id}/unsafe`, {
      authenticity_token: CSRF_TOKEN,
    }))
  }

  return (
    <React.Fragment>
      <div className='MainHeader'>
        <div className='MainHeader__left'>
          <h2 className='MainHeader__h2'>Groups list</h2>
          <p className='MainHeader__left__p'>Total {total.toLocaleString()} groups</p>
        </div>

        {renderOpenAllButton()}
      </div>

      <div className='TableChecker'>
        {renderCheckBoxForCheckAll()}

        {canEdit('Group') &&
          <div className='TableChecker__right'>
            <a
              onClick={bulkSafe}
              disabled={checkIds.length === 0}
              className='TableChecker__right__a TableChecker__right__a--guard'
            />
            <a
              onClick={bulkUnSafe}
              disabled={checkIds.length === 0}
              className='TableChecker__right__a TableChecker__right__a--no-guard'
            />
            <a
              onClick={bulkUnHide}
              disabled={checkIds.length === 0}
              className='TableChecker__right__a TableChecker__right__a--eye'
            />
            <a
              onClick={bulkHide}
              disabled={checkIds.length === 0}
              className='TableChecker__right__a TableChecker__right__a--eye-close'
            />
            <a
              onClick={bulkDelete}
              disabled={checkIds.length === 0}
              className='TableChecker__right__a TableChecker__right__a--trash'
            />
            <a
              onClick={bulkRemoveCover}
              disabled={checkIds.length === 0}
              className='TableChecker__right__a TableChecker__right__a--x-sq'
            />
          </div>
        }
      </div>

      <div className='Table Table--groups-list'>
        {(() => {
          const columnHeaders = [
            {
              label: ''
            },
            {
              label: 'ID',
              sortKey: 'group_id',
            },
            {
              label: 'Cover',
            },
            {
              label: 'Topic',
            },
            {
              label: 'Description',
            },
            {
              label: 'Categories',
            },
            {
              label: 'Owner ID',
            },
            {
              label: 'Generation',
            },
            {
              label: 'Safe Mode',
            },
            {
              label: 'Private',
            },
            {
              label: 'Secret',
            },
            {
              label: 'Users',
              sortKey: 'groups_users_count',
            },
            {
              label: 'Posts',
              sortKey: 'posts_count',
            },
            {
              label: 'Reported',
              sortKey: 'reported_count',
            },
            {
              label: 'Manage',
            }
          ]

          return (
            <div className='Table__row'>
              {columnHeaders.map((header, key) => (
                <TableHeader
                  key={key}
                  header={header}
                />
              ))}
            </div>
          )
        })()}

        {groups.map((group) => (
          <div
            className={className('Table__row', {
              'Table__row--active': checkIds.includes(group.id),
              'Table__row--hiding': !group.showing && enableOpacity,
            })}
            key={group.id}
            data-x={group}
          >
            <div className='Table__col'>
              {renderCheckBox(group)}
            </div>

            <div className='Table__col'>
              <a
                className='Table__a'
                href={`/${gon.currentProvider}/groups/${group.id}`}
              >
                {group.id}
              </a>
            </div>

            <div className='Table__col'>
              <ImageLoader
                klassName='Groups__cover'
                url={get(group, 'coverImage.url')}
              />
            </div>

            <div className='Table__col'>
              <OverflowH
                text={group.topic}
              />
            </div>

            <div className='Table__col'>
              <OverflowH
                text={group.description}
              />
            </div>

            <div className='Table__col'>{get(group, 'groupCategory.name')}</div>

            <div className='Table__col'>
              <DotTooltip
                user={group.owner}
              />
            </div>

            <div className='Table__col'>{group.generation}</div>
            <div className='Table__col'>{group.safeMode ? 'Yes' : 'No'}</div>
            <div className='Table__col'>{group.isPrivate ? 'Yes' : 'No'}</div>
            <div className='Table__col'>{group.secret ? 'Yes' : 'No'}</div>
            <div className='Table__col'>{group.groupsUsersCount}</div>
            <div className='Table__col'>{group.postsCount}</div>
            <div className='Table__col'>{group.reportedCount}</div>

            <div className='Table__col'>
              {canEdit('Group') &&
                <div className='Table__controls'>
                  <a
                    className={`Table__controls__a Table__controls__a--${group.safeMode ? 'no-guard' : 'guard'}`}
                    onClick={group.safeMode ? () => unsafe(group.id) : () => safe(group.id)}
                  />
                  <a
                    className={`Table__controls__a Table__controls__a--${group.showing ? 'eye-close' : 'eye'}`}
                    onClick={group.showing ? () => hide(group.id) : () => unhide(group.id)}
                  />
                  <a
                    className='Table__controls__a Table__controls__a--pencil'
                    href={`/${gon.currentProvider}/groups/${get(group, 'id')}/edit`}
                  />
                  <ModalDelete
                    type='group'
                    id={get(group, 'id')}
                    klassName='Table__controls__a Table__controls__a--trash'
                    reload
                    hideText
                  />
                  <ModalDeleteImage
                    id={get(group, 'id')}
                    resourceType='group'
                    imageType='cover'
                    hideText
                  />
                </div>
              }
            </div>
          </div>
        ))}
      </div>
    </React.Fragment>
  );
}

export default GroupsList;
